import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingCart, Menu, X } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { Logo } from "@/components/layout/Logo";
import { useSettings } from "@/lib/settings";
import { CurrencyToggle } from "@/components/util/CurrencyToggle";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/workstations", label: "Workstations", commerce: true },
  { to: "/software", label: "Software", commerce: true },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

// Permanently-sticky header (Feb 2026 final). All scroll-animation
// experiments have been removed — no scroll listener, no threshold,
// no reveal/hide transform, no opacity animation, no mode state.
// The header is `position: fixed` at the top of the viewport at all
// times. `SiteLayout` pads `<main>` with pt-20 to clear it, so there
// is no content clipping and no layout shift.
export const Navbar = () => {
  const { count, setOpen } = useCart();
  const [mobile, setMobile] = useState(false);
  const navigate = useNavigate();
  const s = useSettings();
  // Website Repositioning (Sep 2026) — Workstations/Software nav links
  // only render while the CMS public-commerce flag is ON.
  const links = s?.public_commerce_workstations_software_enabled === true
    ? navLinks
    : navLinks.filter((l) => !l.commerce);

  return (
    <header
      data-testid="main-navbar"
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b border-[#262626] bg-[#0F0F0F]/95 shadow-[0_8px_24px_-8px_rgba(0,0,0,0.6)]"
    >
      {/* Sep 2026 wide-desktop pass: below 1440px this bar is byte-identical
          to the approved laptop header (max-w-1400, standard gutters). At
          1440px+ it becomes a true 3-zone header via Tailwind min-[1440px]
          utilities (these provably outrank the lg: utilities in cascade —
          a plain-CSS rule in index.css lost to lg:px-10 on padding, so the
          alignment math lives in arbitrary-value classes instead): the LOGO
          sits exactly on the hero content's left grid line — same math as
          the hero shell: max((100vw-1560px)/2, 0) + clamp(2.5rem,4vw,4rem)
          — the CART keeps its approved fluid right gutter
          clamp(2.5rem,3.5vw,4rem) (capped 64px so ultra-wide monitors never
          pin it to the glass), and the center nav is absolutely centered
          to the VIEWPORT (not the leftover space between logo/cart). The
          logo scales 1.18x via transform at 1440+ (layout height unchanged
          — the h-20 bar never grows). */}
      <div className="nav-shell-wide relative max-w-[1400px] min-[1440px]:max-w-none mx-auto px-4 sm:px-6 lg:px-10 min-[1440px]:pl-[calc(max((100vw-1720px)/2,0px)+clamp(2.5rem,4vw,4rem))] min-[1440px]:pr-[clamp(2.5rem,3.5vw,4rem)] h-20 flex items-center justify-between gap-3 sm:gap-8">
        <Link
          to="/"
          data-testid="logo-link"
          onClick={() => window.scrollTo(0, 0)}
          className="flex items-center shrink-0 min-[1440px]:scale-[1.18] min-[1440px]:origin-left"
        >
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-7 min-[1440px]:absolute min-[1440px]:left-1/2 min-[1440px]:-translate-x-1/2">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              end={l.to === "/"}
              className={({ isActive }) =>
                `text-[13px] font-semibold tracking-[0.18em] uppercase cursor-pointer transition-colors duration-200 ${
                  isActive ? "text-[#FF6600]" : "text-white/85 hover:text-[#FF6600]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <CurrencyToggle testId="nav-currency-toggle" className="hidden lg:inline-flex" />
          <button
            data-testid="cart-toggle-btn"
            onClick={() => setOpen(true)}
            aria-label="Open cart"
            className="relative w-11 h-11 rounded-full border border-[#262626] bg-[#141414] hover:border-[#FF6600] grid place-items-center transition-colors"
          >
            <ShoppingCart className="w-[18px] h-[18px] text-white" />
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-[#FF6600] text-black text-[11px] font-bold grid place-items-center"
              >
                {count}
              </span>
            )}
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobile((v) => !v)}
            aria-label="Menu"
            className="lg:hidden w-11 h-11 rounded-full border border-[#262626] bg-[#141414] grid place-items-center"
          >
            {mobile ? <X className="w-[18px] h-[18px]" /> : <Menu className="w-[18px] h-[18px]" />}
          </button>
        </div>
      </div>

      {mobile && (
        <div className="lg:hidden border-t border-[#1F1F1F] bg-[#0F0F0F]">
          <div className="max-w-[1400px] mx-auto px-4 py-6 flex flex-col gap-1">
            {links.map((l) => (
              <button
                key={l.to}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => {
                  setMobile(false);
                  navigate(l.to);
                }}
                className="text-left py-3 text-[14px] font-semibold tracking-[0.18em] uppercase text-white/85 hover:text-[#FF6600]"
              >
                {l.label}
              </button>
            ))}
            <div className="pt-4 mt-2 border-t border-[#1F1F1F] flex items-center justify-between">
              <span className="text-[11px] font-bold tracking-[0.18em] uppercase text-[#7A7A7A]">Currency</span>
              <CurrencyToggle testId="mobile-currency-toggle" />
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
